import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit2, ChevronRight, Shield, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { useLocation } from "wouter";

export default function RoleManagement() {
  const utils = trpc.useUtils();
  const [, setLocation] = useLocation();
  const { data: roles = [], isLoading } = trpc.roles.list.useQuery();
  const { data: permissionSchema } = trpc.roles.getPermissionSchema.useQuery();

  const [isEditing, setIsEditing] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    permissions: [] as string[],
  });

  const createMutation = trpc.roles.create.useMutation({
    onSuccess: () => {
      utils.roles.list.invalidate();
      setIsEditing(false);
      resetForm();
      toast.success("Perfil criado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao criar perfil")),
  });

  const updateMutation = trpc.roles.update.useMutation({
    onSuccess: () => {
      utils.roles.list.invalidate();
      setIsEditing(false);
      resetForm();
      toast.success("Perfil atualizado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao atualizar perfil")),
  });

  const deleteMutation = trpc.roles.delete.useMutation({
    onSuccess: () => {
      utils.roles.list.invalidate();
      toast.success("Perfil excluido com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao excluir perfil")),
  });

  const resetForm = () => {
    setEditingRole(null);
    setFormData({ name: "", slug: "", description: "", permissions: [] });
  };

  const openCreate = () => {
    resetForm();
    setIsEditing(true);
  };

  const openEdit = (role: any) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      slug: role.slug,
      description: role.description || "",
      permissions: [...(role.permissions || [])],
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    resetForm();
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast.error("Nome do perfil e obrigatorio");
      return;
    }

    if (editingRole) {
      updateMutation.mutate({
        id: editingRole.id,
        name: editingRole.isSystem ? undefined : formData.name,
        description: formData.description,
        permissions: formData.permissions,
      });
    } else {
      if (!formData.slug.trim()) {
        toast.error("Slug e obrigatorio");
        return;
      }
      createMutation.mutate({
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        permissions: formData.permissions,
      });
    }
  };

  const togglePermission = (key: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleGroupAll = (groupPermissions: string[]) => {
    const allSelected = groupPermissions.every((p) => formData.permissions.includes(p));
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !groupPermissions.includes(p)),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...groupPermissions])],
      }));
    }
  };

  const groups = permissionSchema?.groups ?? [];

  return (
    <PermissionGuard permission="users:manage_roles">
    <DashboardLayout>
      <div className="space-y-6">
        {!isEditing ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setLocation("/settings?tab=users")}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">Perfis de Acesso</h1>
                  <p className="text-muted-foreground text-sm mt-1">
                    Gerencie os perfis e permissoes de acesso ao sistema
                  </p>
                </div>
              </div>
              <Button onClick={openCreate} className="sm:self-auto self-start">
                <Plus className="w-4 h-4 mr-2" />
                Novo Perfil
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {isLoading ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  Carregando perfis...
                </div>
              ) : roles.length === 0 ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  Nenhum perfil cadastrado
                </div>
              ) : (
                roles.map((role: any) => (
                  <Card key={role.id} className="relative">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Shield className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <CardTitle className="text-base truncate">{role.name}</CardTitle>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <code className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                                {role.slug}
                              </code>
                              {role.isSystem && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  Sistema
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(role)}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          {!role.isSystem && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                if (confirm(`Excluir o perfil "${role.name}"?`)) {
                                  deleteMutation.mutate({ id: role.id });
                                }
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {role.description || "Sem descricao"}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {(role.permissions as string[])?.length || 0} permissoes
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Button variant="ghost" size="sm" onClick={cancelEdit}>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold truncate">
                    {editingRole ? `Editar Perfil: ${editingRole.name}` : "Novo Perfil"}
                  </h1>
                  <p className="text-muted-foreground text-sm mt-1">
                    Configure o nome, descricao e permissoes do perfil
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={cancelEdit}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingRole ? "Salvar Alteracoes" : "Criar Perfil"}
                </Button>
              </div>
            </div>

            <div className="max-w-4xl space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Informacoes Basicas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium">Nome</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ex: Gerente de Vendas"
                        disabled={editingRole?.isSystem}
                      />
                      {editingRole?.isSystem && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Perfis de sistema nao podem ser renomeados
                        </p>
                      )}
                    </div>
                    {!editingRole && (
                      <div>
                        <label className="text-sm font-medium">Slug (identificador)</label>
                        <Input
                          value={formData.slug}
                          onChange={(e) => setFormData({
                            ...formData,
                            slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""),
                          })}
                          placeholder="ex: gerente-vendas"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Apenas letras minusculas, numeros, - e _
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium">Descricao</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descreva as responsabilidades deste perfil..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-base">
                      Permissoes ({formData.permissions.length} selecionadas)
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, permissions: [...(permissionSchema?.allPermissions ?? [])] })}
                      >
                        Selecionar todas
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, permissions: [] })}
                      >
                        Limpar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 border rounded-lg p-3">
                    {groups.map((group: any) => {
                      const groupKeys = group.permissions.map((p: any) => p.key);
                      const selectedCount = groupKeys.filter((k: string) =>
                        formData.permissions.includes(k)
                      ).length;
                      const allGroupSelected = selectedCount === groupKeys.length;

                      return (
                        <Collapsible key={group.key}>
                          <div className="flex items-center gap-2 py-1">
                            <Checkbox
                              checked={allGroupSelected}
                              onCheckedChange={() => toggleGroupAll(groupKeys)}
                              aria-label={`Selecionar todas de ${group.label}`}
                            />
                            <CollapsibleTrigger className="flex items-center gap-2 flex-1 hover:bg-muted/50 rounded px-2 py-1 -ml-1 transition-colors">
                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
                              <span className="text-sm font-medium">{group.label}</span>
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-auto">
                                {selectedCount}/{groupKeys.length}
                              </Badge>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent>
                            <div className="ml-8 space-y-1.5 pb-2">
                              {group.permissions.map((perm: any) => (
                                <label
                                  key={perm.key}
                                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 rounded px-2 py-1 transition-colors"
                                >
                                  <Checkbox
                                    checked={formData.permissions.includes(perm.key)}
                                    onCheckedChange={() => togglePermission(perm.key)}
                                  />
                                  <span className="text-muted-foreground">{perm.label}</span>
                                </label>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingRole ? "Salvar Alteracoes" : "Criar Perfil"}
                </Button>
                <Button variant="outline" onClick={cancelEdit}>
                  Cancelar
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
